import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FieldArrayType, FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { validateValue } from 'src/app/core/validators/custom-validators';

interface IShipmentIdentifier {
  identifier: string;
  value: string;
  id: string;
}

@Component({
    selector: 'ups-shipment-identifier-table',
    templateUrl: './shipment-identifier-table.type.component.html',
    styleUrls: ['./shipment-identifier-table.type.component.scss'],
    standalone: false
})
export class ShipmentIdentifierTableTypeComponent extends FieldArrayType implements OnInit, OnDestroy {
  public fields: FormlyFieldConfig[] = [];
  public tableHeaders: { label: string }[] = [];
  public identifiersForm: FormGroup = new FormGroup({});
  public type = 'text';

  private destroy$ = new Subject<boolean>();

  constructor(private fb: FormBuilder, private translateService: TranslateService) {
    super();
  }

  ngOnInit() {
    this.tableHeaders = this.to['tableHeaders'];
    this.to['fields'].forEach((control: FormlyFieldConfig) => {
      let defaultValue: any = null;
      const key: string = control.key?.toString() || '';

      if (control.templateOptions && control.templateOptions['default']) {
        defaultValue = control.templateOptions['default'];
      }

      if (key === 'shipmentIdentifierValue') {
        this.identifiersForm.addControl(
          key,
          this.fb.control(defaultValue, [Validators.required, validateValue(this.identifiersForm)])
        );
      } else {
        this.identifiersForm.addControl(key, this.fb.control(defaultValue, [Validators.required]));
      }
    });
    this.fields = this.to['fields'];

    this.identifiersForm
      .get('shipmentIdentifier')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.identifiersForm.get('shipmentIdentifierValue')?.setValue(null);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(false);
    this.destroy$.complete();
  }

  get identifiers(): IShipmentIdentifier[] {
    return this.formControl.value;
  }

  get identifierRequired(): boolean {
    return !!this.field.templateOptions?.required && !this.identifiers.length;
  }

  public addIdentifier(): void {
    if (!this.identifiersForm.valid) {
      return;
    }
    const options = this.fields[0].templateOptions?.options as any[];
    const selectedValue = options.find((x) => x.value?.toString() === this.identifiersForm.value.shipmentIdentifier);

    this.add(this.formControl.controls.length, {
      label: selectedValue.label,
      value: this.identifiersForm.value.shipmentIdentifierValue,
      id: selectedValue.id,
      apiName: selectedValue.value,
    });
    this.identifiersForm.reset({
      shipmentIdentifier: null,
      shipmentIdentifierValue: null,
    });
  }

  public drop(index: number) {
    this.formControl.removeAt(index);
    this.model.splice(index, 1);
  }

  public getErrorMessages(control: AbstractControl | null | undefined): string {
    let error = 'false';
    if (control?.errors) {
      if (control.errors['confirmationInvalid']) {
        error = this.translateService.instant('createCase.invalidConfirmation');
      } else if (control.errors['infoNoticeInvalid']) {
        error = this.translateService.instant('createCase.invalidInfoNotice');
      } else if (control.errors['pickupRecordShipmentInvalid']) {
        error = this.translateService.instant('createCase.invalidPickupRecordShipment');
      } else if (control.errors['trackingNumberInvalid']) {
        error = this.translateService.instant('createCase.invalidTrackingNumber');
      } else if (control.errors['proNumberInvalid']) {
        error = this.translateService.instant('createCase.invalidProNumber');
      } else if (control.errors['maxLengthError']) {
        error = this.translateService.instant('createCase.maxLengthAlternativeFormat', { length: 40 });
      }
    }
    return error;
  }
}
